import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Tag,
  Modal,
  Descriptions,
  Typography,
  Row,
  Col,
  Popconfirm,
  message,
  Tooltip,
  Divider,
  Statistic
} from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  CalculatorOutlined
} from "@ant-design/icons";
import { getRateLogs, getRateLogById, deleteRateLog } from "../../api/rateApi";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const RateLogList = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    dateRange: null
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: filters.search,
        status: filters.status
      };

      if (filters.dateRange && filters.dateRange.length === 2) {
        params.start_date = filters.dateRange[0].format("YYYY-MM-DD");
        params.end_date = filters.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await getRateLogs(params);
      setLogs(response.getAllRateLogs || []);
      setPagination(prev => ({
        ...prev,
        total: response.totalRateLogs || 0
      }));
    } catch (error) {
      message.error("Failed to fetch rate logs");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleTableChange = (paginationConfig) => {
    setPagination(prev => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    }));
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusChange = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    setFilters(prev => ({ ...prev, dateRange: dates }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleViewDetail = async (record) => {
    setDetailLoading(true);
    setDetailModalVisible(true);
    try {
      const response = await getRateLogById(record.id);
      setSelectedLog(response);
    } catch (error) {
      message.error("Failed to fetch rate log details");
      setDetailModalVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRateLog(id);
      message.success("Rate log deleted successfully");
      fetchLogs();
    } catch (error) {
      message.error("Failed to delete rate log");
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at)
    },
    {
      title: "Calculated By",
      dataIndex: "calculated_by",
      key: "calculated_by",
      width: 150,
      render: (user) => user?.username || "System"
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 150,
      render: (customer) => customer?.name || "-"
    },
    {
      title: "Item Name",
      dataIndex: "item_name",
      key: "item_name",
      width: 200,
      ellipsis: true
    },
    {
      title: "Dimensions (LxWxH)",
      key: "dimensions",
      width: 140,
      render: (_, record) => (
        <Text>
          {record.length} x {record.width} x {record.height} {record.unit_type}
        </Text>
      )
    },
    {
      title: "Ply",
      dataIndex: "ply",
      key: "ply",
      width: 60,
      align: "center",
      render: (ply) => <Tag color="blue">{ply}</Tag>
    },
    {
      title: "Final Rate",
      dataIndex: "final_rate",
      key: "final_rate",
      width: 120,
      align: "right",
      render: (rate) => (
        <Text strong style={{ color: "#52c41a" }}>
          ₹{rate?.toFixed(2) || "0.00"}
        </Text>
      ),
      sorter: (a, b) => (a.final_rate || 0) - (b.final_rate || 0)
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => {
        const colors = {
          calculated: "processing",
          approved: "success",
          rejected: "error",
          quoted: "warning"
        };
        return <Tag color={colors[status] || "default"}>{status?.toUpperCase()}</Tag>;
      }
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this rate log?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const renderDetailModal = () => {
    if (!selectedLog) return null;

    const inputSnapshot = selectedLog.input_snapshot || {};
    const outputSnapshot = selectedLog.output_snapshot || {};

    return (
      <Modal
        title={
          <Space>
            <CalculatorOutlined />
            <span>Rate Calculation Details</span>
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedLog(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={900}
        loading={detailLoading}
      >
        {selectedLog && (
          <>
            {/* Header Info */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="Final Rate"
                  value={selectedLog.final_rate || 0}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Calculated By"
                  value={selectedLog.calculated_by?.username || "System"}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Date"
                  value={dayjs(selectedLog.created_at).format("DD/MM/YYYY HH:mm")}
                />
              </Col>
            </Row>

            <Divider>Basic Information</Divider>
            <Descriptions bordered size="small" column={3}>
              <Descriptions.Item label="Item Name" span={3}>
                {selectedLog.item_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Customer">
                {selectedLog.customer?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Subcategory">
                {selectedLog.subcategory || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag>{selectedLog.status?.toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Length">{selectedLog.length}</Descriptions.Item>
              <Descriptions.Item label="Width">{selectedLog.width}</Descriptions.Item>
              <Descriptions.Item label="Height">{selectedLog.height}</Descriptions.Item>
              <Descriptions.Item label="Unit Type">{selectedLog.unit_type}</Descriptions.Item>
              <Descriptions.Item label="Ply">{selectedLog.ply}</Descriptions.Item>
              <Descriptions.Item label="Casting">{selectedLog.casting || "-"}</Descriptions.Item>
            </Descriptions>

            <Divider>Calculated Dimensions</Divider>
            <Descriptions bordered size="small" column={4}>
              <Descriptions.Item label="RS">{selectedLog.rs?.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="CS">{selectedLog.cs?.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="N">{selectedLog.n?.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="M">{selectedLog.m?.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Dec Up">{selectedLog.dec_up}</Descriptions.Item>
              <Descriptions.Item label="Dec Across">{selectedLog.dec_across}</Descriptions.Item>
              <Descriptions.Item label="Sheet Size">
                {selectedLog.sheet_length} x {selectedLog.sheet_width}
              </Descriptions.Item>
              <Descriptions.Item label="Sheet Area (sqm)">
                {selectedLog.sheet_area_sqm?.toFixed(4)}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Cost Breakdown</Divider>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Paper Cost">
                ₹{selectedLog.paper_cost?.toFixed(2) || "0.00"}
              </Descriptions.Item>
              <Descriptions.Item label="Conversion Cost">
                ₹{selectedLog.conversion_cost?.toFixed(2) || "0.00"}
              </Descriptions.Item>
              <Descriptions.Item label="Printing Cost">
                ₹{selectedLog.printing_cost?.toFixed(2) || "0.00"}
              </Descriptions.Item>
              <Descriptions.Item label="Coating Cost">
                ₹{selectedLog.coating_cost?.toFixed(2) || "0.00"}
              </Descriptions.Item>
              <Descriptions.Item label="Die Cost">
                ₹{selectedLog.die_cost?.toFixed(2) || "0.00"}
              </Descriptions.Item>
              <Descriptions.Item label="Box Rate">
                ₹{selectedLog.box_rate?.toFixed(2) || "0.00"}
              </Descriptions.Item>
            </Descriptions>

            {/* Layers Information */}
            {selectedLog.layers && selectedLog.layers.length > 0 && (
              <>
                <Divider>Layer Configuration</Divider>
                <Table
                  dataSource={selectedLog.layers}
                  columns={[
                    { title: "Layer", dataIndex: "layerId", key: "layerId" },
                    { title: "Type", dataIndex: "layerType", key: "layerType" },
                    { title: "Material", dataIndex: "materialType", key: "materialType" },
                    { title: "GSM", dataIndex: "gsm", key: "gsm" },
                    { title: "BF", dataIndex: "bf", key: "bf" }
                  ]}
                  pagination={false}
                  size="small"
                  rowKey="layerId"
                />
              </>
            )}

            {/* Printing Configuration */}
            {selectedLog.is_printing && selectedLog.printing_config && (
              <>
                <Divider>Printing Configuration</Divider>
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="Printing Type">
                    {selectedLog.printing_config.printingType || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Colors">
                    {selectedLog.printing_config.colors?.length || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="Coverage">
                    {selectedLog.printing_config.coverage || "-"}%
                  </Descriptions.Item>
                  <Descriptions.Item label="Rate">
                    ₹{selectedLog.printing_config.rate || 0}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            {/* Coating Configuration */}
            {selectedLog.is_coating && selectedLog.coating_config && (
              <>
                <Divider>Coating Configuration</Divider>
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="Coating Types" span={2}>
                    {selectedLog.coating_config.coatings?.join(", ") || "-"}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            {/* Notes */}
            {selectedLog.notes && (
              <>
                <Divider>Notes</Divider>
                <Text>{selectedLog.notes}</Text>
              </>
            )}
          </>
        )}
      </Modal>
    );
  };

  return (
    <Card
      title={
        <Space>
          <CalculatorOutlined />
          <span>Rate Calculation History</span>
        </Space>
      }
      extra={
        <Button icon={<ReloadOutlined />} onClick={fetchLogs}>
          Refresh
        </Button>
      }
    >
      {/* Filters */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input.Search
            placeholder="Search item name, notes..."
            allowClear
            onSearch={handleSearch}
            prefix={<SearchOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Select
            placeholder="Status"
            allowClear
            style={{ width: "100%" }}
            onChange={handleStatusChange}
            value={filters.status || undefined}
          >
            <Option value="calculated">Calculated</Option>
            <Option value="approved">Approved</Option>
            <Option value="rejected">Rejected</Option>
            <Option value="quoted">Quoted</Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <RangePicker
            style={{ width: "100%" }}
            onChange={handleDateRangeChange}
            value={filters.dateRange}
          />
        </Col>
      </Row>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={logs}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} calculations`
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        onRow={(record) => ({
          onClick: () => handleViewDetail(record),
          style: { cursor: "pointer" }
        })}
      />

      {/* Detail Modal */}
      {renderDetailModal()}
    </Card>
  );
};

export default RateLogList;
